import { SlidersHorizontal } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useEscapeToClose, useOutsideDismiss } from '@/components/ui/overlay';
import { cn } from '@/lib/utils';

/**
 * One control in the header for the verbs a page keeps standing (issue #89).
 *
 * The job page and the task page each used to spend their first, most-read
 * band on the things a person does least often there: Amend, Pause and Cancel
 * on a job; Cancel, Pause and Change this task on a task. Three bordered bands
 * and three sentences, forty-odd words, above the goal and the plan a person
 * actually opened the page to read. Now it is one affordance beside the title,
 * and the bands move into the popover it opens - each verb keeps its button
 * and its sentence, there is just room for both without spending the page's
 * first screen on them.
 *
 * It is the header's own control rather than a page region, so it renders
 * through `ViewHeader`'s `actions` slot and returns `null` itself when there is
 * nothing to offer - the caller does not have to know that in advance.
 *
 * Exported for issue #100's peek panel, which repeats the same verbs in less
 * room and wants the same trigger and the same rows rather than a fourth
 * implementation of "a menu of the things you can do to this".
 */
export function ActionsPopover({
  label = 'Actions',
  align = 'end',
  children,
  className,
}: {
  label?: string;
  align?: 'start' | 'end';
  /** Either the rows themselves, or a function that gets a `close` to call
   * once a verb inside has done what it opened the popover to do (Amend, which
   * hands off to a section of the page rather than confirming in place). */
  children: ReactNode | ((close: () => void) => ReactNode);
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const close = () => setOpen(false);

  useEscapeToClose(open, close);
  useOutsideDismiss(wrap, open, close);

  return (
    <div ref={wrap} className={cn('relative', className)}>
      <Button
        variant="outline"
        size="sm"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="max-sm:h-9 max-sm:px-3"
      >
        <SlidersHorizontal aria-hidden />
        {label}
      </Button>
      {open ? (
        <div
          role="dialog"
          aria-label={label}
          className={cn(
            'bg-pop dy-pop-in absolute top-[calc(100%+6px)] z-40 w-[min(23rem,90vw)]',
            'max-h-[70vh] overflow-y-auto rounded-lg border shadow-pop',
            align === 'start' ? 'left-0' : 'right-0',
          )}
        >
          {typeof children === 'function' ? children(close) : children}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One verb inside the popover: a hairline above it once it is not the first,
 * an optional sentence that says what it will do before it does it, and
 * whatever the verb's own control is - `PauseBriefActions`, `CancelOnly` and
 * the rest already draw their own row with its own `px-gutter`, so this adds
 * no horizontal padding of its own and does not double theirs. That is also
 * why the sentence carries its own `px-gutter`: it is prose beside a row, not
 * inside one.
 */
export function ActionRow({
  sentence,
  children,
  className,
}: {
  sentence?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('border-line/70 border-t first:border-t-0', className)}>
      {sentence ? <p className="text-fg-3 px-gutter pt-2.5 text-xs">{sentence}</p> : null}
      {children}
    </div>
  );
}
